import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { notification, Upload } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import WorkOrderDetailsContext from './WorkOrderDetailsContext';
import ConfirmScanModal from './modals/ConfirmScanModal';
import { PropTypes } from 'mobx-react';
import ThumbnailViewer from '../../components/General/ThumbnailViewer';
import { detectTiffFile } from '../../shared/utils';

const ImageUploadContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 10px;
  margin-top: 20px;
`;

const ImagePreviewWrapper = styled.div`
  border-radius: 2px;
  position: relative;
  width: 112px;
  height: 112px;
  padding-left: 5px;
  &:hover {
    .delete-btn {
      display: flex;
    }
  }
`;

const DeleteButton = styled.div`
  position: absolute;
  top: -10%;
  right: -10%;
  background: black;
  border-radius: 56%;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 24px;
  height: 24px;
  padding: 2px;

  .anticon {
    font-size: 13px;
  }
`;

const UploadButton = styled(Upload)`
  .ant-upload {
    width: 112px;
    height: 112px;
    border: 1px dashed #d9d9d9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .ant-upload-text {
      text-align: center;
    }

    .anticon-plus {
      font-size: 13px;
    }
  }
`;

const UploadAreaContainer = styled.div`
  border: 1px solid #d9d9d9;
  padding: 16px;
  border-radius: 4px;
  margin-top: 10px;
  background-color: #fff;
`;

const UploadButtonWrapper = styled.div`
  width: 112px;
  height: 112px;
  padding-left: 5px;
`;

const ImageUploader = ({
  onImageUploaded,
  wo_files,
  childRef,
  forScan = [],
}) => {
  const [fileList, setFileList] = useState([]);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pending, setPending] = useState([]);
  const { onClickImage } = useContext(WorkOrderDetailsContext);

  useEffect(() => {
    if (wo_files.wo_images && wo_files.wo_images.length > 0) {
      const initialFileList = wo_files.wo_images.map((url, index) => ({
        uid: `old-${index}`,
        name: `Image ${index}`,
        status: 'done',
        url,
        thumbUrl: url,
      }));

      setFileList(initialFileList);
    }
  }, [wo_files.wo_images]);

  const handlePreview = (file) => {
    const isGreaterThan2M = file.size / 1024 / 1024 > 10;

    if (isGreaterThan2M) {
      return false;
    } else {
      // Create blob URL instead of base64
      const blobUrl = URL.createObjectURL(file);
      const fileWithUrl = {
        ...file,
        url: blobUrl,
        uid: file.uid || `file-${Date.now()}`,
      };
      setFileList([...fileList, fileWithUrl]);
    }

    onImageUploaded(file);
  };

  if (childRef) {
    childRef({
      getImages: () => fileList,
      handlePreview,
      setFileList,
    });
  }

  const handleChange = ({ file, fileList: newFileList }) => {
    const filteredList = newFileList.filter(
      (f) => !f.size || f.size / 1024 / 1024 <= 12
    );
    if (newFileList.length > filteredList.length) {
      notification.open({
        message: <div style={{ color: 'red' }}>Error</div>,
        description: 'File size must be less than 12MB.',
      });
    }

    setFileList(filteredList);
  };

  const handleRemove = (file) => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(newFileList);
    onImageUploaded(file, 'removed');
  };

  const uploadButton = (
    <div
      style={{
        width: '92px',
        height: '92px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <PlusOutlined />
      <div style={{ fontSize: '13px' }}>Upload</div>
    </div>
  );

  const giveFilesMeta = (files) => {
    return files.map((file) => ({
      uid: file.uid,
      name: file.name,
      status: 'done',
      url: URL.createObjectURL(file),
      originFileObj: file,
      type: file.type,
      size: file.size,
    }));
  };

  const handleConfirmScan = (scanFiles) => {
    if (pending) {
      const noScan = pending.filter(
        (pendingFile) => !scanFiles.includes(pendingFile)
      );
      const filesWithMeta = giveFilesMeta(noScan);
      const scanFilesWithMeta = giveFilesMeta(scanFiles);
      setFileList((curr) => [...curr, ...filesWithMeta]);
      noScan.forEach((file) => handlePreview(file));
      setPending([]);
      forScan(scanFilesWithMeta);
      setShowConfirmationModal(false);
    }
  };

  return (
    <UploadAreaContainer>
      <div>
        <b>Add images:</b>
      </div>
      <ImageUploadContainer>
        {fileList.map((file) => {
          return (
            <ImagePreviewWrapper key={file.uid} style={{ cursor: 'pointer' }}>
              <ThumbnailViewer
                media={file.url}
                width={112}
                height={112}
                onClick={() => {
                  onClickImage(file.url);
                }}
              />
              <DeleteButton
                className="delete-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemove(file);
                }}
              >
                <CloseOutlined />
              </DeleteButton>
            </ImagePreviewWrapper>
          );
        })}
        {fileList.length < 18 && (
          <UploadButtonWrapper>
            <UploadButton
              accept=".png,.jpg,.jpeg"
              listType="picture-card"
              // fileList={fileList}
              // onChange={handleChange}
              onRemove={handleRemove}
              fileList={[]}
              beforeUpload={async (file) => {
                // Check if file is actually a TIFF (even if extension is wrong)
                const isTiff = await detectTiffFile(file);
                if (isTiff) {
                  notification.error({
                    message: 'TIFF files not allowed',
                    description:
                      'TIFF files are not supported. Upload from SFTP instead.',
                  });
                  return false;
                }

                // Check file size
                const isGreaterThan10M = file.size / 1024 / 1024 > 12;
                if (isGreaterThan10M) {
                  notification.error({
                    message: 'File too large',
                    description: 'File size must be less than 12MB.',
                  });
                  return false;
                }

                setPending((prev) => [...prev, file]);
                if (!showConfirmationModal) setShowConfirmationModal(true);
                return false;
              }}
              showUploadList={false}
            >
              {uploadButton}
            </UploadButton>
          </UploadButtonWrapper>
        )}
      </ImageUploadContainer>
      <ConfirmScanModal
        isVisible={showConfirmationModal}
        onConfirm={(forScan) => handleConfirmScan(forScan)}
        onCancel={() => handleConfirmScan([])}
        files={pending}
      />
    </UploadAreaContainer>
  );
};

ImageUploader.propTypes = {
  forScan: PropTypes.array,
};

export default ImageUploader;
